/**
 * CHG-053 REQ-V53-019: 이슈 상세/편집 — fieldConfig 기반 동적 필드 렌더링
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import FieldRenderer from './FieldRenderer';
import type { FieldConfigSection } from '../../services/issueTypeFieldConfigService';

interface Props {
  issue: Record<string, unknown>;
  sections: FieldConfigSection[];
  isEditing?: boolean;
  onChange?: (fieldKey: string, value: unknown, isSystem: boolean) => void;
  members?: { id: string; name: string }[];
}

/** 이슈에서 필드 값을 가져온다 (시스템 필드: 직접, 커스텀: customFields) */
function getFieldValue(issue: Record<string, unknown>, fieldKey: string, isSystem: boolean): unknown {
  if (isSystem) return issue[fieldKey];
  const cf = (issue.customFields as Record<string, unknown>) ?? {};
  return cf[fieldKey];
}

export default function IssueDetailFields({ issue, sections, isEditing, onChange, members }: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggle = (name: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const visibleFields = section.fields.filter((f) => f.visibility !== 'HIDDEN');
        if (visibleFields.length === 0) return null;

        const isCollapsed = collapsed.has(section.name);

        return (
          <div key={section.name}>
            <button
              onClick={() => toggle(section.name)}
              className="flex items-center gap-1 text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 hover:text-gray-700"
            >
              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              {section.name}
              <span className="font-normal text-gray-400 ml-1">({visibleFields.length})</span>
            </button>

            {!isCollapsed && (
              <div className="grid grid-cols-2 gap-3">
                {visibleFields.map((fc) => (
                  <FieldRenderer
                    key={fc.field.fieldId}
                    fieldKey={fc.field.fieldKey}
                    fieldType={fc.field.fieldType}
                    name={fc.field.name}
                    options={fc.field.options}
                    value={getFieldValue(issue, fc.field.fieldKey, fc.field.isSystem)}
                    onChange={
                      isEditing && onChange
                        ? (val) => onChange(fc.field.fieldKey, val, fc.field.isSystem)
                        : undefined
                    }
                    readOnly={!isEditing}
                    required={fc.visibility === 'REQUIRED'}
                    members={fc.field.fieldType === 'USER' ? members : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
